import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Link, SidebarItem } from '@backstage/core-components';
import {
  Dialog,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import AppsIcon from '@material-ui/icons/Apps';
import CloseIcon from '@material-ui/icons/Close';
import LockIcon from '@material-ui/icons/Lock';
import { usePlatformServices } from '../platform/services';
import type { PlatformService } from '../platform/services';
import { onColour } from '../theme/palette';

const useStyles = makeStyles(theme => ({
  paper: {
    width: 960,
    maxWidth: '94vw',
    padding: theme.spacing(4, 3, 3),
  },
  headRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1, 3, 2),
  },
  head: {
    flex: 1,
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: '2.2px',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1.75),
    padding: theme.spacing(3, 1.5),
    borderRadius: 10,
    textAlign: 'center',
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
      background: theme.palette.action.hover,
      textDecoration: 'none',
    },
  },
  locked: {
    opacity: 0.5,
    cursor: 'not-allowed',
    '&:hover': {
      background: 'none',
    },
  },
  monogram: {
    position: 'relative',
    width: 80,
    height: 80,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: onColour(theme.palette.primary.main),
    fontWeight: 700,
    fontSize: 26,
    background: theme.palette.primary.main,
  },
  lockBadge: {
    position: 'absolute',
    right: -6,
    bottom: -6,
    fontSize: 24,
    borderRadius: '50%',
    background: theme.palette.background.paper,
    color: theme.palette.text.secondary,
  },
  name: {
    fontSize: 18,
    lineHeight: 1.3,
  },
  foot: {
    padding: theme.spacing(2.25, 2, 0.25),
    marginTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  tooltip: {
    // MUI's default is 10px, which reads as a footnote next to tiles this size.
    fontSize: 15,
    lineHeight: 1.4,
    maxWidth: 360,
    padding: theme.spacing(1, 1.5),
  },
  footLink: {
    fontSize: 15,
    fontWeight: 600,
  },
}));

const DrawerItem = ({
  service,
  accessRequestUrl,
  onNavigate,
}: {
  service: PlatformService;
  accessRequestUrl?: string;
  onNavigate: () => void;
}) => {
  const classes = useStyles();

  const inner = (
    <>
      <span
        className={classes.monogram}
        style={service.color ? { background: service.color } : undefined}
        aria-hidden
      >
        {service.monogram}
        {!service.entitled && <LockIcon className={classes.lockBadge} />}
      </span>
      <Typography className={classes.name}>{service.title}</Typography>
    </>
  );

  // A locked entry must not navigate, exactly as `platform/ServiceTile.tsx`
  // does not: dimming it and then honouring the click would walk the user into
  // a system that rejects them - and for a service configured with `url`, into
  // another host's error page rather than a Backstage route. The only
  // destination a locked entry is allowed is the access portal, when one is
  // configured.
  if (!service.entitled) {
    const reason = accessRequestUrl
      ? `${service.system} - you do not have access yet. Request it from the access portal.`
      : `${service.system} - you do not have access yet. Ask your application group admin to grant it.`;

    return (
      <Tooltip title={reason} classes={{ tooltip: classes.tooltip }}>
        {accessRequestUrl ? (
          <a
            className={`${classes.item} ${classes.locked}`}
            href={accessRequestUrl}
            target="_blank"
            rel="noreferrer"
            onClick={onNavigate}
          >
            {inner}
          </a>
        ) : (
          <div className={`${classes.item} ${classes.locked}`}>{inner}</div>
        )}
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={`${service.system} - ${service.menuAction}`}
      classes={{ tooltip: classes.tooltip }}
    >
      <Link
        to={service.menuHref}
        className={classes.item}
        onClick={onNavigate}
        {...(service.menuExternal
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : {})}
      >
        {inner}
      </Link>
    </Tooltip>
  );
};

/**
 * Set once the menu has opened on entry in this browser tab, so it greets the
 * user when they arrive and not again every time they go back to Home.
 */
const ENTRY_KEY = 'aeven.serviceMenu.openedOnEntry';

const openedOnEntry = (): boolean => {
  try {
    return window.sessionStorage.getItem(ENTRY_KEY) !== null;
  } catch {
    // Storage blocked: err towards showing the menu, which is the requirement,
    // over never showing it.
    return false;
  }
};

const markOpenedOnEntry = () => {
  try {
    window.sessionStorage.setItem(ENTRY_KEY, '1');
  } catch {
    // Nothing to do: the menu opens again on the next full page load.
  }
};

/**
 * The Aeven One platform menu (#12238): every platform service, in the main
 * window, opened from "Services" at the top of the sidebar.
 *
 * It is also the first thing a user sees. Only some of the people who sign in
 * are here to provision anything - the rest want Grafana, Wazuh or the docs -
 * so the menu opens on its own when a tab arrives at the front page, and
 * Hybrid Orchestration is one tile in it that leads to the dashboard behind
 * it. It opens once per tab, so Home stays the dashboard afterwards; and only
 * on the front page, so a deep link to a stack is not covered by a menu its
 * user has already answered.
 *
 * A service the user cannot reach is shown locked rather than hidden, so the
 * menu is a map of the platform and not a map of one account. The detail -
 * what each service is for, and how to ask for access - lives on the front
 * page section this links to; here the point is to get somewhere in one click.
 */
export const ServiceDrawer = () => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(() => pathname === '/' && !openedOnEntry());
  const { loading, services, accessRequestUrl } = usePlatformServices();
  // Held back until the services resolve, so the entry menu does not flash
  // open empty for the second the identity lookup takes.
  const visible = open && services.length > 0;

  // Marked in an effect rather than in the state initialiser: StrictMode runs
  // an initialiser twice, and a write in the first run would make the second
  // one decide the menu has already been shown. Keyed on `visible`, not
  // `open`, so a tab left before the services resolved still gets its menu.
  useEffect(() => {
    if (visible) markOpenedOnEntry();
  }, [visible]);

  const close = useCallback(() => setOpen(false), []);

  if (!loading && services.length === 0) {
    return null;
  }

  return (
    <>
      <SidebarItem
        icon={AppsIcon}
        text="Services"
        // Toggles what the user sees: while the services load, `open` can
        // already be true for the entry menu, and flipping it would make the
        // first click do nothing.
        onClick={() => setOpen(!visible)}
      />
      <Dialog
        open={visible}
        onClose={close}
        aria-labelledby="platform-services-title"
        classes={{ paper: classes.paper }}
      >
        <div className={classes.headRow}>
          <Typography
            id="platform-services-title"
            component="h2"
            className={classes.head}
          >
            Platform services
          </Typography>
          <IconButton aria-label="Close" onClick={close}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className={classes.grid}>
          {services.map(service => (
            <DrawerItem
              key={service.id}
              service={service}
              accessRequestUrl={accessRequestUrl}
              onNavigate={close}
            />
          ))}
        </div>
        <div className={classes.foot}>
          {/* The prototype reads "All services and their status", which this
              deliberately does not copy: its tiles carry a hand-written ok/warn
              flag, and nothing here has a service-health source to replace it
              with. What the section below actually adds over this menu is what
              each service is for and whether you can get into it, so that is
              what the link promises. */}
          <Link to="/#services" className={classes.footLink} onClick={close}>
            All services and your access &rarr;
          </Link>
        </div>
      </Dialog>
    </>
  );
};
